import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, FileUp, Loader2, PackageCheck, Plus, ScanText, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdate, money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusBadge } from "@/components/common/StatusBadge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";

const emptyBill = { date: "", gstin: "", freight: 0, other_charges: 0, bill_total: 0, supplier: "" };

export const ReceiveGoodsDialog = ({ open, onOpenChange, onDone, presetPoId }) => {
  const [pos, setPos] = useState([]);
  const [cats, setCats] = useState([]);
  const [poId, setPoId] = useState("");
  const [items, setItems] = useState([]);
  const [invNo, setInvNo] = useState("");
  const [file, setFile] = useState("");
  const [bill, setBill] = useState(emptyBill);
  const [warnings, setWarnings] = useState([]);
  const [reading, setReading] = useState(false);
  const [markFull, setMarkFull] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => {
    if (!open) return;
    setInvNo(""); setFile(""); setItems([]); setPoId(presetPoId || "");
    setBill(emptyBill); setWarnings([]); setMarkFull(false);
    api.get("/purchase-orders/open").then((r) => setPos(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  }, [open, presetPoId]);

  const po = pos.find((p) => p.id === poId);

  // Auto-detect the products from the purchase order the goods came against
  useEffect(() => {
    if (!po) return;
    setItems(po.items.filter((i) => i.pending_qty > 0).map((i) => ({
      product_id: i.product_id || "", name: i.name, qty: i.pending_qty,
      price: i.price, tax_rate: i.tax_rate ?? 18, category: "", uom: "Nos", isNew: false,
    })));
  }, [poId, pos]); // eslint-disable-line

  const setItem = (idx, patch) => setItems((s) => s.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const readBill = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f.name);
    if (!invNo) setInvNo(f.name.replace(/\.[^.]+$/, ""));
    setReading(true);
    setWarnings([]);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("po_id", poId || "");
      const { data } = await api.post("/inventory/ocr-invoice", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      if (data.invoice_number) setInvNo(data.invoice_number);
      setBill({
        date: data.invoice_date || "", gstin: data.supplier_gstin || "",
        freight: data.freight || 0, other_charges: data.other_charges || 0,
        bill_total: data.bill_total || 0, supplier: data.supplier_name || "",
      });
      setWarnings(data.warnings || []);
      if (data.items?.length) {
        setItems(data.items.map((i) => ({
          product_id: i.product_id || "", name: i.name, qty: i.qty, price: i.price,
          tax_rate: i.tax_rate, category: i.category || "", uom: i.uom || "Nos",
          isNew: i.is_new, billName: i.bill_name, poMatched: i.po_matched,
          poOrdered: i.po_ordered_qty,
        })));
        toast.success(`${data.items.length} product line(s) read from ${f.name}`);
      } else {
        toast.warning("No product lines could be read from that bill. Please add them manually.");
      }
    } catch (err) {
      toast.error(errMsg(err, "The bill could not be read. Please enter the details manually."));
    } finally {
      setReading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const lineValue = items.reduce((a, i) => a + Number(i.qty || 0) * Number(i.price || 0), 0);
  const newCount = items.filter((i) => i.isNew).length;

  const submit = async () => {
    const valid = items.filter((i) => i.name && Number(i.qty) > 0);
    if (!valid.length) return toast.error("Add at least one product with a quantity.");
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/receive", {
        po_id: poId, supplier_name: po?.supplier_name || bill.supplier || "",
        supplier_invoice_no: invNo, supplier_invoice_file: file,
        supplier_invoice_date: bill.date, supplier_gstin: bill.gstin,
        freight: Number(bill.freight || 0), other_charges: Number(bill.other_charges || 0),
        bill_total: Number(bill.bill_total || 0), mark_fully_received: markFull,
        items: valid.map((i) => ({
          product_id: i.product_id || "", name: i.name, qty: Number(i.qty),
          price: Number(i.price || 0), tax_rate: Number(i.tax_rate || 0),
          category: i.category || "", uom: i.uom || "Nos",
        })),
      });
      toast.success(
        `${data.received} product line${data.received > 1 ? "s" : ""} added to stock` +
        (data.po_status ? ` \u00b7 PO marked ${data.po_status}` : "")
      );
      if (data.created_products?.length) {
        toast.info(`New product${data.created_products.length > 1 ? "s" : ""} created: ${data.created_products.join(", ")}`);
      }
      onDone?.();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto scroll-slim">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Receive goods from supplier</DialogTitle>
          <DialogDescription>
            Upload a photo or PDF of the supplier bill and we read it for you, or pick the purchase
            order and we fill in the pending products. Edit anything before you save.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Against purchase order">
              <Select value={poId} onValueChange={setPoId}>
                <SelectTrigger data-testid="receive-po"><SelectValue placeholder="Select a purchase order" /></SelectTrigger>
                <SelectContent>
                  {pos.length === 0 && <SelectItem value="none" disabled>No open purchase orders</SelectItem>}
                  {pos.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.number} &middot; {p.supplier_name} &middot; {fdate(p.date)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Supplier invoice number">
              <Input value={invNo} onChange={(e) => setInvNo(e.target.value)} data-testid="receive-invno" placeholder="e.g. SP/2026/1184" />
            </Field>
          </div>

          <div className="rounded-xl border border-primary/25 bg-primary-soft/40 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-primary/40">
                {reading ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <FileUp className="h-4 w-4 text-primary" />}
                {reading ? "Reading the bill\u2026" : "Upload supplier bill (photo or PDF)"}
                <input ref={fileInput} type="file" hidden disabled={reading}
                  accept="image/*,application/pdf" onChange={readBill} data-testid="receive-file" />
              </label>
              {file && <StatusBadge status={file} tone="success" dot={false} />}
              <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-primary" /> Products, quantities, rates, freight and
                the bill total are picked up automatically
              </span>
            </div>

            {(bill.date || bill.gstin || bill.bill_total > 0 || bill.freight > 0) && (
              <div className="mt-3 grid gap-3 sm:grid-cols-4">
                <Field label="Bill date">
                  <Input type="date" value={bill.date} data-testid="bill-date"
                    onChange={(e) => setBill({ ...bill, date: e.target.value })} />
                </Field>
                <Field label="Supplier GSTIN">
                  <Input value={bill.gstin} data-testid="bill-gstin"
                    onChange={(e) => setBill({ ...bill, gstin: e.target.value })} />
                </Field>
                <Field label="Freight / packing">
                  <Input type="number" value={bill.freight} data-testid="bill-freight"
                    onChange={(e) => setBill({ ...bill, freight: e.target.value })} />
                </Field>
                <Field label="Bill total">
                  <Input type="number" value={bill.bill_total} data-testid="bill-total"
                    onChange={(e) => setBill({ ...bill, bill_total: e.target.value })} />
                </Field>
              </div>
            )}
          </div>

          {warnings.length > 0 && (
            <div className="space-y-1.5 rounded-xl border border-warning/40 bg-warning-soft px-4 py-3" data-testid="ocr-warnings">
              <p className="flex items-center gap-2 text-sm font-semibold text-warning">
                <AlertTriangle className="h-4 w-4" /> Please check before you save
              </p>
              {warnings.map((w, i) => (
                <p key={i} className="text-xs text-foreground/80">&bull; {w}</p>
              ))}
            </div>
          )}

          {po && (
            <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm">
              <p className="font-medium">{po.supplier_name} &middot; {po.number}</p>
              <p className="text-xs text-muted-foreground">
                Ordered value {money(po.total)} &middot; expected {fdate(po.expected_delivery)} &middot; current status{" "}
                <b className="text-foreground">{po.status}</b>
              </p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Products received {items.length > 0 && `(${items.length})`}
                {newCount > 0 && <span className="ml-2 text-destructive">{newCount} new item{newCount > 1 ? "s" : ""}</span>}
              </p>
              <Button type="button" size="sm" variant="soft" data-testid="receive-add-line"
                onClick={() => setItems([...items, { product_id: "", name: "", qty: 1, price: 0, tax_rate: 18, category: "", isNew: true }])}>
                <Plus className="h-3.5 w-3.5" /> Add a product not on the PO
              </Button>
            </div>

            {items.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                Upload the supplier bill, select a purchase order, or add the products manually.
              </p>
            ) : items.map((it, idx) => (
              <div key={idx} className="grid gap-3 rounded-xl border border-border bg-muted/30 p-3 sm:grid-cols-12">
                <div className="sm:col-span-5">
                  <Field label={it.isNew ? "Product (new to the catalogue)" : "Product"}>
                    <Input value={it.name} onChange={(e) => setItem(idx, { name: e.target.value })}
                      className={it.isNew ? "border-destructive/50" : undefined}
                      data-testid={`receive-name-${idx}`} placeholder="Product name from the bill" />
                    {it.billName && it.billName !== it.name && (
                      <p className="mt-1 text-[10px] text-muted-foreground">On the bill: {it.billName}</p>
                    )}
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label={it.poOrdered ? `Qty (ordered ${it.poOrdered})` : "Qty received"}>
                    <Input type="number" min="0" value={it.qty} data-testid={`receive-qty-${idx}`}
                      onChange={(e) => setItem(idx, { qty: e.target.value })} />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Cost">
                    <Input type="number" min="0" value={it.price} data-testid={`receive-price-${idx}`}
                      onChange={(e) => setItem(idx, { price: e.target.value })} />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label={it.product_id ? "Existing item" : "Category (new item)"}>
                    {it.product_id ? (
                      <Input value="Matched" disabled />
                    ) : (
                      <Select value={it.category} onValueChange={(v) => setItem(idx, { category: v })}>
                        <SelectTrigger><SelectValue placeholder="Auto" /></SelectTrigger>
                        <SelectContent>
                          {cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                          <SelectItem value="Other Products">Other Products</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                </div>
                <div className="flex items-end sm:col-span-1">
                  <Button type="button" variant="ghost" size="iconSm" className="text-destructive"
                    onClick={() => setItems(items.filter((_, i) => i !== idx))}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {items.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2">
                <span className="num text-xs text-muted-foreground">
                  Line value <b className="text-foreground">{money(lineValue)}</b>
                  {Number(bill.bill_total) > 0 && <> &middot; bill total <b className="text-foreground">{money(Number(bill.bill_total))}</b></>}
                </span>
                {po && (
                  <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
                    <Checkbox checked={markFull} onCheckedChange={(v) => setMarkFull(!!v)} data-testid="receive-mark-full" />
                    Mark this purchase order fully received
                  </label>
                )}
              </div>
            )}

            <p className="text-[11px] text-muted-foreground">
              Products that are not already in your catalogue are created automatically, along with a new
              category if needed. The purchase order is marked <b>Fully Received</b> once every line is in,
              otherwise it stays <b>Partially Received</b>.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="success" onClick={submit} disabled={busy || reading || items.length === 0} data-testid="receive-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : reading ? <ScanText className="h-4 w-4" /> : <PackageCheck className="h-4 w-4" />}
            Receive and add to stock
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
